"use client";

import { Translate } from "@/components/Translate";
import { Close24 } from "@/components/icons/Close24";
import { Button } from "@/components/ui/Button";
import type { FormattedFilter } from "@frend-digital/centra/filters";
import { useUrlSearchParams } from "@frend-digital/ui/next";
import styles from "./Filters.module.css";
import type { FormattedFilterGroup } from "@/lib/depict/filters";

const safeDecodeURIComponent = (value: string): string => {
	try {
		let decoded = value;
		let previousDecoded = "";

		while (decoded !== previousDecoded && decoded.includes("%")) {
			previousDecoded = decoded;
			try {
				decoded = decodeURIComponent(decoded);
			} catch {
				break;
			}
		}

		return decoded;
	} catch (error) {
		console.warn(`Failed to decode URI component: ${value}`, error);
		return value;
	}
};

export const SelectedFilters = ({ filters }: { filters: FormattedFilterGroup[] }) => {
	const searchParams = useUrlSearchParams({
		replace: true,
	});

	const selectedFilters = filters.flatMap((filter) =>
		searchParams
			.getAll(filter.key)
			.filter((value) => {
				if (filter.key === "in_stock" && value === "false") {
					return false;
				}
				return true;
			})
			.map((value) => {
				const decodedValue = safeDecodeURIComponent(value);
				return {
					field: filter.key,
					label: filter.values.find((v) => v.value === decodedValue)?.name || decodedValue,
					value: decodedValue,
					originalValue: value,
				};
			}),
	);

	const removeFilter = (field: string, originalValue: string) => {
		const currentValues = searchParams.getAll(field);
		const updatedValues = currentValues.filter((val) => val !== originalValue);

		if (updatedValues.length > 0) {
			searchParams.set(field, updatedValues.join(","));
		} else {
			searchParams.delete(field);
		}
	};

	const clearAll = () => {
		filters.forEach((filter) => {
			searchParams.delete(filter.key);
		});
	};

	return (
		<div className={styles.selectedFilters} data-has-filters={selectedFilters.length > 0}>
			{selectedFilters.map((filter) => (
				<Button
					key={filter.field + filter.originalValue}
					type="button"
					className={styles.filterTag}
					onClick={() => removeFilter(filter.field, filter.originalValue)}
				>
					<div className={styles.remove}>
						<Close24 width={16} height={16} color="var(--black)" />
					</div>
					<span>
						{filter.field === "in_stock" ? (
							<Translate>In stock</Translate>
						) : (
							<Translate>{filter.label}</Translate>
						)}
					</span>
				</Button>
			))}
			{selectedFilters.length > 0 && (
				<button type="button" className={styles.clearAll} onClick={clearAll}>
					<span>
						<Translate>Clear all filters</Translate>
					</span>
				</button>
			)}
		</div>
	);
};
